import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps, Carrier, KeyLabel, KeyLabelObject } from 'src/Library/Types';
import {
  Modal,
  Form,
  Button,
  message,
  Spin,
  Row,
  Col,
  Select,
  InputNumber,
  Radio,
  Icon,
  Checkbox,
  AutoComplete,
  Upload,
} from 'src/Library/AntdLibComponents';
import { useGenericState } from 'src/Library/UseGenericState';
import { FormComponentProps } from 'antd/lib/form';
import { ShipmentData } from 'src/components/pages/shipment-execution/SE.Types';
import { connect } from 'react-redux';
import { FileVGMModalStyles } from './FileVGMModal.styles';
import { renderLabelAndInput, renderSelectDropDown } from 'src/Library/Common';
import FileVGMContainerAndWeighbridge from './FileVGMContainerAndWeighbridge';
import { isValidArray, clone, stops, isValidString, getDateValue, isValidNumber } from 'src/Library/Helper';
import {
  requestVGMMethod,
  fetchVGMPendingContainers,
  fetchVGMAuthorisedPersons,
  saveVgmRequest,
  saveVgmDraftRequest,
  fetchVGMDraft,
  fetchVGMThirdPartyAuthLetter,
  addVGMThirdPartyAuthLetter,
  retryVgmRequest,
  fetchVGMUserAssignee,
} from 'src/api/shipmentExecution';
import { useDebounce } from 'src/hooks/useDebounce';
import isEmpty from 'lodash/isEmpty';
import { uploadToS3, getS3Links } from 'src/api/invoice';
import { getFileExtension } from 'src/utils/utils';
import Protip from 'src/components/pages/Enquiry/Booking/Protip';
import { Drawer } from 'antd';
import OrderDetailsIcon from '../../../create-shipment/create-shipment.svg/order-details';
import ContainerIcon from '../../../create-shipment/create-shipment.svg/container';
import StakeholderIcon from '../../../create-shipment/create-shipment.svg/stakeholder';

interface IProps extends StylesProps<ReturnType<typeof FileVGMModalStyles>>,
  FormComponentProps {
  isVisible: boolean;
  onCancel: () => void;
  onSubmit: (isSuccessful?: boolean, requestBody?, successResponse?) => void;
  shipmentData: ShipmentData;
  shippingLines: Carrier [];
  vgmValuationMethods: KeyLabel [];
  pendingContainers: number;
  userType: string;
  retryRequestData?;
  isReSubmitting?: boolean; // to resubmit the request
  setDocumentTab: (isDraft?: boolean, tabToOpen?: string) => void;
  isDocumentRequestTab?: boolean;
  onSaveDraft: () => void;
  isRetryRequest?: boolean; // to retry containers failed during submission
  productIMOClassCodeMap: KeyLabelObject;
  gotoDocumentRequest: () => void;
}

interface StateProps {
  weighmentDetails;
  isFetchingContainers?: boolean;
  containerNoList;
  isPrefillingAllContainers?: boolean;
  authorisedPersonsList?;
  isFetchingAuthorisedPersons: boolean;
  searchTerm?: string;
  isSubmitting?: boolean;
  draftData?;
  isSavingDraft?: boolean;
  isLoading?: boolean;
  selectedAuthPerson?;
  thirdPartyAuthLetterData?;
  uploading?: boolean;
  authFileName?: string;
  authFileUrl?: string;
  shipperAuthLetterMappingId?: string;
  isDraftProtipVisible?: boolean;
  trackingContainers;
}

const FileVGMModal = (props: IProps): React.ReactElement => {

  const {
    classes,
    isVisible,
    onCancel,
    form,
    onSubmit,
    shipmentData,
    shippingLines,
    vgmValuationMethods,
    pendingContainers,
    userType,
    isReSubmitting,
    retryRequestData,
    setDocumentTab,
    isDocumentRequestTab,
    onSaveDraft,
    isRetryRequest,
    productIMOClassCodeMap,
    gotoDocumentRequest,
  } = props;

  const [state, setState] = useGenericState<StateProps>({
    weighmentDetails: [{}],
    containerNoList: [],
    isFetchingAuthorisedPersons: false,
    trackingContainers: [],
  });

  const {
    weighmentDetails,
    isFetchingContainers,
    containerNoList,
    isPrefillingAllContainers,
    isFetchingAuthorisedPersons,
    authorisedPersonsList,
    searchTerm,
    isSubmitting,
    draftData,
    isSavingDraft,
    selectedAuthPerson,
    thirdPartyAuthLetterData,
    uploading,
    authFileName,
    authFileUrl,
    shipperAuthLetterMappingId,
    isDraftProtipVisible,
    isLoading,
    trackingContainers,
  } = state;

  const {
    getFieldDecorator,
    setFieldsValue,
    getFieldValue,
  } = form;

  const debouncedSearchAuthorisedPersons = useDebounce(searchTerm, 300);

  React.useEffect(() => {
    getAuthorisedPersons(debouncedSearchAuthorisedPersons);
  }, [debouncedSearchAuthorisedPersons]);

  React.useEffect(() => {
    if (isReSubmitting) {
      setState({ draftData: retryRequestData });
    } else {
      setPrefillingObject();
    }
    fetchContainers();
  }, []);

  const fetchContainers = async () => {
    const response = await fetchVGMPendingContainers(shipmentData?.id);
    if (response?.isSuccess) {
      setState({ trackingContainers: response.data || [], containerNoList: response.data || [] });
    }
  }

  const fetchAssignedUserDetails = async () => {
    const response = await fetchVGMUserAssignee(shipmentData?.id);
    if (response?.isSuccess) {
      setFieldsValue({
        authorisedPersonName: response?.data?.name,
        authorisedPersonMobileno: response?.data?.mobileno,
      })
    } else {
      message.error(response.errorMessage);
    }
  };

  React.useEffect(() => {
    prefillDataInForm();
  }, [draftData]);

  const setPrefillingObject = async () : Promise<void> => {
    setState({ isLoading: true });
    if (userType !== 'SHIPPER') {
      const responses : any = await Promise.all([
        fetchVGMDraft(shipmentData?.id),
        fetchVGMThirdPartyAuthLetter(shipmentData?.shipperId),
      ])
      responses.forEach((response, index) => {
        if (!response.isSuccess) {
          message.error(response.errorMessage)
        }
      });
      setState({
        ...(!isEmpty(responses[0]?.data) && {
          draftData: responses[0]?.data,
        }),
        ...(!isEmpty(responses[1]?.data) && {
          thirdPartyAuthLetterData: responses[1]?.data,
        }),
        isLoading: false,
      });
      if (!isEmpty(responses[1]?.data)) {
        setState({
          authFileName: responses[1]?.data?.documentName,
          authFileUrl: responses[1]?.data?.documentUrl,
          shipperAuthLetterMappingId: responses[1]?.data?.id,
        })
      }
    } else {
      const response = await fetchVGMDraft(shipmentData?.id);
      if (response.isSuccess && !isEmpty(response?.data)) {
        if (!response?.data?.authorisedPerson?.name) {
          await fetchAssignedUserDetails();
        }
        setState({ draftData: response.data, isLoading: false });
      } else {
        await fetchAssignedUserDetails();
        setState({ isLoading: false });
      }
    }
  }

  const prefillDataInForm = () : void => {
    if (isEmpty(draftData)) return;
    setFieldsValue({
      carrierId: draftData.carrierId,
      vessel: draftData.vessel,
      bookingNumber: draftData.bookingNumber,
      voyage: draftData.voyage,
      polId: !isEmpty(draftData.pol) ? {
        key: draftData.pol?.id,
        label: `${draftData.pol?.name} (${draftData.pol?.code})`
      } : (!isEmpty(draftData.retryPol) ? draftData.retryPol : undefined),
      authorisedPersonName: draftData.authorisedPerson?.name || getFieldValue('authorisedPersonName'),
      authorisedPersonMobileno: draftData.authorisedPerson?.mobileno || getFieldValue('authorisedPersonMobileno'),
      authorisedPersonDesignation: draftData.authorisedPerson?.designation,
      vgmValuationMethod: draftData.valuationMethod,
      autofillWeighbridgeDetail: draftData.autofillWeighbridgeDetail,
    });
    setState({
      weighmentDetails: draftData.containerWeighmentDetails,
      selectedAuthPerson: {
        id: draftData.authorisedPerson?.id,
        name: draftData.authorisedPerson?.name,
      },
    });
    () => {
      setFieldsValue({
        weighmentDetails: draftData.containerWeighmentDetails,
      });
    }
  }

  const handleSaveDraft = async () : Promise<void> => {
    const formValues = form.getFieldsValue();
    if (!isValidString(formValues?.authorisedPersonName) &&
      (isValidString(formValues?.authorisedPersonMobileno) ||
        isValidString(formValues?.authorisedPersonDesignation))
    ) {
        message.error('Authorised person name must be provided');
        return;
    }
    setState({ isSavingDraft: true });
    const body = createApiCallObject(formValues);
    const response = await saveVgmDraftRequest(shipmentData?.id, draftData?.id, body)
    if (response.isSuccess) {
      setState({ isDraftProtipVisible: true, isSavingDraft: false });
    } else {
      message.error(response.errorMessage);
      setState({ isSavingDraft: false });
    }
  };

  const handleSubmit = () : void => {
    form.validateFieldsAndScroll(async (err, values) => {
      if (err) {
        return;
      }
      if (!shipperAuthLetterMappingId && userType !== 'SHIPPER') {
        message.error('Please upload Authorization Letter by Shipper');
      }
      setState({ isSubmitting: true });
      const body = createApiCallObject(values);
      // !isRetryRequest - this is to resubmit the containers with errors during submission
      const response = (isReSubmitting && !isRetryRequest) ?
        await retryVgmRequest(shipmentData?.id, draftData?.id, {
          ...body,
          previousContainerNumber: retryRequestData?.containerWeighmentDetails?.[0]?.containerNumber,
        }) : await saveVgmRequest(shipmentData?.id, draftData?.id, body)
      if (response.isSuccess) {
        onSubmit(true, {
          ...body,
          retryPol: values?.polId,
          id: draftData?.id,
          valuationMethod: values?.vgmValuationMethod,
        }, response.data);
        setState({ isSubmitting: false });
      } else {
        message.error(response.errorMessage);
        setState({ isSubmitting: false });
      }
    });
  }

  const createApiCallObject = (formValues) => {
    const body = {
      ...formValues,
      authorisedPersonId: formValues.authorisedPersonName?.trim() === selectedAuthPerson?.name?.trim() ?
        selectedAuthPerson?.id : undefined,
      authorisedPersonName: formValues.authorisedPersonName?.trim(),
      polId: formValues.polId?.key,
      weighmentDetails: formValues.weighmentDetails?.map(item => ({
        ...item,
        cargoType: item.cargoType || 'GENERAL',
        cscPlateMaxWeightLimit: isValidNumber(item.cscPlateMaxWeightLimit?.value) ?
          item.cscPlateMaxWeightLimit : undefined,
        weight: isValidNumber(item.weight?.value) ? item.weight : undefined,
        weighingTimestamp: item.weighingTimestamp ? {
          dateString: getDateValue(item.weighingTimestamp)?.format('YYYY-MM-DD'),
          time: getDateValue(item.weighingTimestamp)?.format('HH:mm'),
        } : undefined,
      })),
      ...(userType !== 'SHIPPER' && {
        shipperAuthLetterMappingId,
      }),
    };
    return body;
  }

  const labelInputProps = {
    colSpan: {
      left: 7,
      right: 17,
    },
    form,
    classes,
    disabled: isLoading,
  };

  const renderCarrierSelect = () : React.ReactElement => {
    return (
      <React.Fragment>
        <Col span={7} className={classes.label}>
          Shipping Line*
        </Col>
        <Col span={17}>
        <Form.Item>
          {form.getFieldDecorator('carrierId', {
            initialValue: shipmentData?.carrierId,
            rules: [{
              required: true, message: 'Required',
            }],
          })(
            <Select
              getPopupContainer={(trigger: any) => trigger.parentNode}
              className={classes.selectField}
              showSearch
              disabled={true}
              allowClear
              optionFilterProp="children"
              placeholder="Select Shipping Line"
            >
              {shippingLines?.map(item => item.type === 'SEA' && (
                <Select.Option key={item.dbId}>{item.name}</Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>
      </Col>
      </React.Fragment>
    );
  };

  const renderPol = () : React.ReactElement => {
    const {
      originPolId,
      originPolCode,
      originPolName,
      originPortId,
      originPortCode,
      originPortName,
    } = shipmentData;
    return (
      <Col span={8}>
        <Col span={7} className={classes.label}>POL*</Col>
        <Col span={17}>
          <Form.Item style={{ marginBottom: '12px' }}>
            {getFieldDecorator('polId', {
              initialValue: (originPolId && originPolCode && originPolName) ? {
                key: originPolId,
                label: `${originPolName} (${originPolCode})`,
              } : {
                key: originPortId,
                label: `${originPortName} (${originPortCode})`,
              },
            })(
              <Select
                getPopupContainer={(trigger: any) => trigger.parentNode}
                className={classes.selectField}
                showSearch
                labelInValue
                disabled={true}
                placeholder="Select POL"
              />
            )}
          </Form.Item>
        </Col>
      </Col>
    );
  };

  const renderBookingDetails =  () : React.ReactElement => {
    return (
      <Row className={classes.box}>
        <Col span={4} className={classes.contentHeading}>
          <div className={classes.boxIcon}>
            <OrderDetailsIcon  className={classes.boxIcon}/>
          </div>
          Booking <br/> Details
        </Col>
        <Col span={20}>
          <Row>
            <Col span={8}>
              {renderCarrierSelect()}
            </Col>
            <Col span={2} />
            <Col span={8} style={{ display: 'flex' }}>
              {renderLabelAndInput({
                ...labelInputProps,
                label: 'Vessel',
                key: 'vessel',
                placeholder: 'Enter Vessel',
                rules: [
                  { max: 50, message: 'Maximum 50 characters allowed' },
                ],
                disabled: isReSubmitting || isLoading,
              })}
            </Col>
          </Row>
          <Row>
            <Col span={8} style={{ display: 'flex' }}>
              {renderLabelAndInput({
                ...labelInputProps,
                label: 'Booking No*',
                key: 'bookingNumber',
                initialValue: shipmentData?.bookingNumber || undefined,
                placeholder: 'Enter Booking No',
                rules: [
                  { required: true, message: 'Required' },
                  { max: 20, message: 'Maximum 20 characters allowed' }
                ],
                disabled: isValidString(shipmentData?.bookingNumber) || isReSubmitting
              })}
            </Col>
            <Col span={2} />
            <Col span={8}>
              {renderLabelAndInput({
                ...labelInputProps,
                label: 'Voyage',
                key: 'voyage',
                placeholder: 'Enter Voyage',
                rules: [
                  { max: 20, message: 'Maximum 20 characters allowed' }
                ],
                disabled: isReSubmitting || isLoading,
              })}
            </Col>
          </Row>
          <Row>{renderPol()}</Row>
        </Col>
      </Row>
    );
  };

  const handleNameSelection = (options) => {
    setFieldsValue({
      authorisedPersonMobileno: options?.props?.mobileno,
      authorisedPersonDesignation: options?.props?.designation,
    });
    setState({
      selectedAuthPerson: {
        id: options?.props?.id,
        name: options?.props?.name,
      },
    })
  }

  const renderAuthorisedPerson =  () : React.ReactElement => {
    return (
      <Row className={classes.box}>
        <Col span={4} className={classes.contentHeading}>
          <div className={classes.boxIcon}>
            <StakeholderIcon  className={classes.boxIcon}/>
          </div>
          Authorised <br/> Person
        </Col>
        <Col span={20}>
          <Row>
            <Col span={6}>
              <Col span={5} className={classes.label}>Name*</Col>
              <Col span={19}>
                <Form.Item style={{ marginBottom: '12px' }}>
                  {getFieldDecorator('authorisedPersonName', {
                    rules: [
                      { required: true, message: 'Required' },
                      { max: 200, message: 'Maximum 200 characters allowed' },
                    ],
                  })(
                    <AutoComplete
                      style={{ fontSize: '12px' }}
                      disabled={isReSubmitting || isLoading}
                      placeholder="Enter Name"
                      onSearch={(searchTerm) => setState({ searchTerm })}
                      onFocus={() => setState({ searchTerm: '' })}
                      dropdownRender={menu => renderSelectDropDown(menu, isFetchingAuthorisedPersons)}
                      onSelect={(val, options) => handleNameSelection(options)}
                    >
                      {authorisedPersonsList?.map((person) => (
                        <AutoComplete.Option
                          key={person.id}
                          value={person.name}
                          { ...person }
                        >
                          {person?.name}
                        </AutoComplete.Option>
                      ))}
                    </AutoComplete>
                  )}
                </Form.Item>
              </Col>
            </Col>
            <Col span={1} />
            <Col span={6} style={{ display: 'flex' }}>
              {renderLabelAndInput({
                ...labelInputProps,
                colSpan: {
                  left: 6,
                  right: 18,
                },
                label: 'Mobile*',
                key: 'authorisedPersonMobileno',
                placeholder: 'Enter Number',
                rules: [
                  { required: true, message: 'Required' },
                  { max: 50, message: 'Maximum 50 characters allowed' },
                ],
                disabled: isReSubmitting || isLoading,
              })}
            </Col>
            <Col span={1} />
            <Col span={7} style={{ display: 'flex' }}>
              {renderLabelAndInput({
                ...labelInputProps,
                label: 'Designation*',
                key: 'authorisedPersonDesignation',
                placeholder: 'Enter Designation',
                rules: [
                  { required: true, message: 'Required' },
                  { max: 200, message: 'Maximum 200 characters allowed' }
                ],
                disabled: isReSubmitting || isLoading,
              })}
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  const renderProtip = () : React.ReactElement => {
    return (
      <Protip
      visible={isDraftProtipVisible}
      success={'Great News!'}
      message1={<span>
        Form has been successfully saved to draft. <a onClick={gotoDocumentRequest}>
          Click here</a> to view saved draft.
      </span>}
      message2={'Now you are being redirected to Tasks view'}
      okay={'Okay'}
      onOkay={() => {
        if (isDocumentRequestTab) {
          onSaveDraft();
        }
        onSubmit();
      }}
    />
    );
  }

  const s3LinkRequest = async ({ file }): Promise<void> => {
    setState({
      uploading: true,
    });
    let shipperAuthLetterMappingId;
    let authFileName;
    const extension = getFileExtension(file.name);
    const response = await getS3Links({
      extension,
      fileName: file.name,
      document_type: file.type,
      initialPath: 'ShipmentExecution',
    });
    let authFileUrl: string = '';
    if (response.isSuccess) {
      const upload = await uploadToS3(response.data.post, extension, file);
      if (upload.isSuccess) {
        authFileUrl = response.data.get;
        const addAuthLetterResponse = await addVGMThirdPartyAuthLetter({
          shipperId: shipmentData?.shipperId,
          url: authFileUrl,
          name: file.name,
        });
        if (addAuthLetterResponse.isSuccess) {
          shipperAuthLetterMappingId = addAuthLetterResponse?.data?.id;
          authFileName = addAuthLetterResponse?.data?.documentName;
          authFileUrl = addAuthLetterResponse?.data?.documentUrl;
        }
      }
      
    } else {
      message.error(response.errorMessage);
    }
    setState({
      authFileUrl,
      authFileName,
      shipperAuthLetterMappingId,
      uploading: false,
    });
  };

  const renderShipperInfo =  () : React.ReactElement => {
    return (
      <Row className={classes.box}>
        <Col span={4} className={classes.contentHeading}>Shipper</Col>
        <Col span={20}>
          <Row>
            <Col span={8}>
              {renderLabelAndInput({
                ...labelInputProps,
                label: 'Shipper Name*',
                key: 'shipper',
                placeholder: 'Enter Shipper',
                rules: [
                  { required: true, message: 'Required' },
                ],
                disabled: true,
                initialValue: shipmentData?.shipperName,
              })}
            </Col>
            <Col span={2} />
            {!thirdPartyAuthLetterData?.shipperOdexId && <Col span={8} style={{ display: 'flex' }}>
              <Col span={14} className={classes.label}>
                Authorization Letter by Shipper*
              </Col>
              <Col span={12}>
                <Upload
                  showUploadList={false}
                  multiple={false}
                  customRequest={file => s3LinkRequest(file)}
                  disabled={isReSubmitting}
                >
                  <Button loading={uploading} disabled={isReSubmitting}>
                    <Icon type="upload" /> Upload
                  </Button>
                </Upload>
                {!isReSubmitting && authFileName && authFileUrl && shipperAuthLetterMappingId &&
                  <div style={{ fontSize: '12px', marginTop: '12px' }}>
                    {authFileName}
                    {<Icon type="close" style={{ color: 'red', marginLeft: '8px' }}
                      onClick={() => setState({ authFileName: '', authFileUrl: '' })} />}
                  </div>}
              </Col>
            </Col>}
          </Row>
        </Col>
      </Row>
    );
  };

  const handleRemoveContainer = (key) => {
    const weighmentDetails = form.getFieldValue('weighmentDetails');
    const clonedweighmentDetails = isValidArray(weighmentDetails) ? clone(weighmentDetails) : [];
    clonedweighmentDetails?.splice(key, 1);
    setState({ weighmentDetails: clonedweighmentDetails || [] });
    setFieldsValue({ weighmentDetails: clonedweighmentDetails || [{}] });
  }

  const handleAddMoreContainer = () : void => {
    const containerCount = getFieldValue('containerCount');
    const containerAddedCount = weighmentDetails?.length || 0;
    let containerToBeAddedCount = containerCount;
    if ((containerCount + containerAddedCount) > pendingContainers) {
      containerToBeAddedCount = (pendingContainers - containerAddedCount);
    }
    if (containerToBeAddedCount !== containerCount) {
      message.error(`This booking consists of maximum ${pendingContainers} container(s)`, 5);
    }
    if (!containerToBeAddedCount || containerToBeAddedCount <= 0) return;
    const clonedweighmentDetails = isValidArray(weighmentDetails) ? clone(weighmentDetails) : [];
    for (let i = 0; i < containerToBeAddedCount; i = i + 1) {
      clonedweighmentDetails.push({});
    };
    message.success(`${containerToBeAddedCount} more container(s) added successfully`);
    setState({
      weighmentDetails: clonedweighmentDetails,
    });
    setFieldsValue({
      containerCount: undefined,
    });
  };

  const handleRequestVGMMethod = async () : Promise<void> => {
    const response = await requestVGMMethod();
    if (response.isSuccess) {
      message.success('VGM Valuation Method requested successfully');
    } else {
      message.error(response.errorMessage);
    };
  };

  const getVGMValuationDescription = (key) : React.ReactElement => {
    switch (key) {
      case 'M1': return (
        <span style={{ cursor: 'default' }} onClick={(e) => stops(e)}>Weighing the packed container.</span>
      );
      case 'M2': return (
        <span style={{ cursor: 'default' }} onClick={(e) => stops(e)}>
          Weighing all the cargo and contents of the container then adding it to the container’s tare weight. <br/>
          This feature is currently not available,
          <a onClick={() => handleRequestVGMMethod()}> Click here </a>
          to request for future filings. 
        </span>
      );
      default: return <span />;
    }
  }

  const renderVGMValuationMethod = () : React.ReactElement => {
    return (
      <div style={{ borderBottom: '1px solid rgba(0,0,0,0.15)' }}>
        <div className={classes.label}>VGM Valuation Method</div>
        <Form.Item style={{ marginBottom: 0 }}>
          {getFieldDecorator('vgmValuationMethod', {
            initialValue: 'M1',
          })(
            <Radio.Group className={classes.flexColumn}>
              {vgmValuationMethods?.map(item  => (
                <Radio value={item.key} style={{ margin: '12px 0' }} key={item.key} disabled={item.key !== 'M1'}>
                  <span style={{ display: 'inline-flex', color: 'rgba(0, 0, 0, 0.65)' }}>
                    {item.label} -&nbsp;{getVGMValuationDescription(item.key)}
                  </span>
                </Radio>
              ))}
            </Radio.Group>
          )}
        </Form.Item>
      </div>
    );
  };

  const getContainers = async (prefillAllContainer?) => {
    let containerList = isValidArray(trackingContainers) ? clone(trackingContainers) : [];
    if (!isValidArray(trackingContainers)) {
      setState({
        isFetchingContainers: true,
        ...(prefillAllContainer && {
            isPrefillingAllContainers: true,
          }),
        });
      const response = await fetchVGMPendingContainers(shipmentData?.id);
      if (response.isSuccess) {
        containerList = response?.data
      } else {
        setState({ isFetchingContainers: false });
      }
    }
    const containers = containerList?.map(item => ({
      containerNumber: item.containerNumber,
      containerSize: item.containerSize || undefined,
      containerType: item.containerType || undefined,
      weighBridgeRegistrationNumber: undefined,
      weighBridgeAddress: undefined,
      weighBridgeSlipNumber: undefined,
      weighingTimestamp: undefined,
      weight: {
        value: undefined,
        unit: 'kg',
      },
      cscPlateMaxWeightLimit: {
        value: undefined,
        unit: 'kg',
      },
      unoNo: undefined,
      imoNo: productIMOClassCodeMap[shipmentData?.cargoSubtype],
    }));
    const weighments = isValidArray(containers) ? containers.slice(0, pendingContainers) : weighmentDetails;
    setState({
      containerNoList: containers || [],
      isFetchingContainers: false,
      ...(prefillAllContainer && {
        weighmentDetails: weighments,
        isPrefillingAllContainers: false,
      }),
    });
    if (prefillAllContainer) {
      setFieldsValue({
        weighmentDetails: weighments,
      });
    }
  }

  const renderAutofillWeighbridgeDetails = () : React.ReactElement => {
    return (
      <div>
        <Form.Item style={{ marginBottom: 0 }}>
          {getFieldDecorator('autofillWeighbridgeDetail', {
            initialValue: draftData?.autofillWeighbridgeDetail || false,
            valuePropName: 'checked',
          })(
            <Checkbox
              onChange={(e) => {
                if (e.target.checked) {
                  const weighmentDetailsAdded = getFieldValue('weighmentDetails')
                  const weighments = weighmentDetailsAdded?.map(item => ({
                    ...item,
                    weighBridgeRegistrationNumber: undefined,
                    weighBridgeAddress: undefined,
                    weighBridgeSlipNumber: undefined,
                    weighingTimestamp: undefined,
                    weight: { value: undefined, unit: 'kg' },
                    cscPlateMaxWeightLimit: { value: undefined, unit: 'kg' },
                  }));
                  setState({ weighmentDetails: weighments });
                  setFieldsValue({ weighmentDetails: weighments });
                }
              }}
              disabled={isLoading}
            >
              Autofill weighbridge details
            </Checkbox>
          )}
        </Form.Item>
        <div className={classes.autofillInfo} style={{ margin: '-10px 0 0 22px' }}>
          Enabling this feature require that you fill the container number and we will fetch
          weighbridge details on successful submission of the form.
        </div>
      </div>
    );
  }

  const getAuthorisedPersons = async (queryString = '') => {
    setState({
      isFetchingAuthorisedPersons: true,
    });
    const response = await fetchVGMAuthorisedPersons(queryString);
    if (response.isSuccess) {
      setState({
        authorisedPersonsList: response.data || [],
        isFetchingAuthorisedPersons: false,
      });
    } else {
      message.error(response.errorMessage)
      setState({ isFetchingAuthorisedPersons: false });
    }
  }

  const renderContainerAndWeighbridgeDetails = () : React.ReactElement => {
    return (
      <Row className={classes.box}>
        <Col span={4} className={classes.contentHeading}>
          <div className={classes.boxIcon}>
            <ContainerIcon className={classes.boxIcon} />
          </div>
          Container &amp; <br />Weighbridge Details
        </Col>
        <Col span={20}>
          {renderVGMValuationMethod()}
          <div className={classes.multipleContainerInfo}>
            <Icon type="info-circle" theme="filled" style={{ margin: '2px 8px 0 0' }} />
            We are enabling the option to add multiple containers in one single VGM request.
            You can add all the containers from the booking and fill in the necessary details.
          </div>
          {!isReSubmitting && trackingContainers?.length >= pendingContainers && <div>
          <span
            className={classes.addMore}
            style={{ marginBottom: '6px', fontWeight: 600 }}
            onClick={() => getContainers(true)}
          >Click here </span>
            to add all
            <span style={{ fontWeight: 600 }}> {pendingContainers} container(s) </span> details from the Booking
            {isValidString(shipmentData?.bookingNumber) &&
              <span style={{ fontWeight: 600 }}> #{shipmentData?.bookingNumber}</span>}
            {isPrefillingAllContainers ? <Spin style={{ marginLeft: '8px' }} /> : ''}
          </div>}
          {renderAutofillWeighbridgeDetails()}
          <Row>
            {weighmentDetails?.map((item, index) => (
              <div key={index} style={{ backgroundColor: '#FAFAFA',  margin: '16px 24px 0 0' }}>
                <FileVGMContainerAndWeighbridge
                  form={form}
                  containerKey={index}
                  weighmentDetails={weighmentDetails}
                  handleRemoveContainer={handleRemoveContainer}
                  shipmentData={shipmentData}
                  containerNoList={containerNoList}
                  getContainers={getContainers}
                  isLoading={isLoading}
                  isWeighbridgeDetailsAutofilled={getFieldValue('autofillWeighbridgeDetail')}
                  draftData={draftData}
                />
              </div>
            ))}
          </Row>
          {!isReSubmitting && <div className={classes.flexRow} style={{ marginTop: '12px' }}>
            <span className={classes.contentLabel} style={{ marginRight: '12px' }}>Add More Containers</span>
            <Form.Item style={{ width: '6%', margin: '0 8px 0 0' }}>
              {getFieldDecorator('containerCount')(
                <InputNumber
                  placeholder="Count"
                  precision={0}
                  min={0}
                  className={classes.inputNumberField}
                  disabled={isLoading}
                />
              )}
            </Form.Item>
            <Button
              className={classes.addContainerButton}
              onClick={handleAddMoreContainer}
              size="small"
              disabled={weighmentDetails?.length === pendingContainers}
              style={{ marginRight: '8px' }}
            >Add</Button>
          </div>}
        </Col>
      </Row>
    );
  };

  const renderSubmitButton = () => {
    return (
      <Button
        type="primary"
        onClick={handleSubmit}
        disabled={isLoading || isSavingDraft}
        className={classes.submitButton}
        loading={isSubmitting}
      >
        Submit
      </Button>
    )
  }

  const renderSaveDraftButton = () => {
    if (isReSubmitting) {
      return;
    }

    return (
      <Button
        className={classes.saveDraftButton}
        type="ghost"
        onClick={() => handleSaveDraft()}
        disabled={isLoading || isSubmitting}
        loading={isSavingDraft}
      >
        Save Draft
      </Button>
    );
  }

  const renderHeader = (title: string) => {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div className = {classes.header}>
          <Icon
            type="close"
            onClick={onCancel}
          />
          <span
            style={{ marginLeft: 10 }}>
            {title}
          </span>
        </div>
        <div>
          {renderSaveDraftButton()}
          {renderSubmitButton()}
        </div>
      </div>
    );
  }

  return (
    <Drawer
      visible={isVisible}
      width={"85%"}
      className={classes.main}
      title={renderHeader("File VGM")}
      onClose={onCancel}
      closable={false}
    >
      {isLoading && (
        <div
          style={{
            height: '100%',
            width: '100%',
            textAlign: 'center',
            top: '30%',
            zIndex: 1200,
            position: 'absolute',
          }}
        >
          <Spin size="large" />
        </div>
      )}
      {renderBookingDetails()}
      {userType !== 'SHIPPER' && renderShipperInfo()}
      {renderAuthorisedPerson()}
      {renderContainerAndWeighbridgeDetails()}
      {isDraftProtipVisible && renderProtip()}
    </Drawer>
  );
};

const mapStateToProps = ({ masterData, shipmentExecutionReducer }, ownProps) => ({
  shippingLines: masterData?.globalShippingLines,
  vgmValuationMethods: masterData?.odexMasterData?.vgmValuationMethods,
  pendingContainers: shipmentExecutionReducer.siPendingCount?.VGM?.pendingContainerCount,
  userType: masterData.userData.type,
  productIMOClassCodeMap: masterData.odexMasterData?.productIMOClassCodeMap,
});

export default connect(
  mapStateToProps,
  null,
)(React.memo(withStyles(FileVGMModalStyles)(Form.create()(FileVGMModal))));
